import type { Problem } from '@unitly/contracts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: Record<string, string>;
  constructor(status: number, problem: Partial<Problem> | null) {
    super(problem?.title ?? (status === 0 ? 'Sei offline. Controlla la connessione.' : 'Si è verificato un errore.'));
    this.status = status;
    this.code = problem?.code ?? 'unknown';
    this.errors = problem?.errors ?? {};
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T>(path: string, method: Method = 'GET', body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'x-unitly-csrf': '1',
        ...(method !== 'GET' && { 'content-type': 'application/json' }),
      },
      body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiError(0, null);
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

/** Like `api`, but sends a file as multipart/form-data instead of JSON (no content-type header: the browser sets the boundary). */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('file', file);
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'x-unitly-csrf': '1' }, body });
  } catch {
    throw new ApiError(0, null);
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const qs = (params: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

/** Like `apiUpload`, for big files: XHR instead of fetch so the caller can show upload progress (0–1). */
export function apiUploadWithProgress<T>(path: string, file: File, onProgress: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/v1${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-unitly-csrf', '1');
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError(0, null));
    xhr.onload = () => {
      let data: unknown = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* empty or non-JSON body */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, data as Partial<Problem> | null));
    };
    const body = new FormData();
    body.append('file', file);
    xhr.send(body);
  });
}
